import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { Capacitor } from '@capacitor/core';
import { isLiberiaApiEnabled, trackLiberiaNavigation } from '@/utils/liberiaApi';
import { useUserRole } from './useUserRole';

interface TrackNavigationParams {
  geobloc?: string;
  geobloc_id?: string;
  city?: string;
  state?: string;
  country?: string;
  zipcode?: string;
  address_id?: string;
  page_type?: string;
  metadata?: Record<string, any>;
}

export const useNavigationTracking = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { role } = useUserRole();
  const sessionTokenRef = useRef<string | null>(null);
  const lastTrackedRef = useRef<string>('');

  useEffect(() => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = crypto.randomUUID();
    }
  }, []);

  const trackNavigation = async (params: TrackNavigationParams) => {
    try {
      if (!isLiberiaApiEnabled() || !sessionTokenRef.current) {
        return;
      }

      const trackingKey = `${location.pathname}-${location.search}-${params.geobloc || ''}-${params.city || ''}-${params.address_id || ''}`;
      if (lastTrackedRef.current === trackingKey) {
        return;
      }
      lastTrackedRef.current = trackingKey;

      const platform = Capacitor.getPlatform();
      const isNative = Capacitor.isNativePlatform();

      const enhancedMetadata = {
        ...params.metadata,
        tracked_address_id: params.address_id || null,
        platform,
        is_native: isNative,
        is_mobile: /Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
        screen_width: window.innerWidth,
        screen_height: window.innerHeight,
        device_pixel_ratio: window.devicePixelRatio,
      };

      await trackLiberiaNavigation({
        session_token: sessionTokenRef.current,
        geobloc: params.geobloc || null,
        geobloc_id: params.geobloc_id || null,
        city: params.city || null,
        state: params.state || null,
        country: params.country || null,
        zipcode: params.zipcode || null,
        address_id: params.address_id || null,
        page_type: params.page_type || 'page_view',
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
        metadata: enhancedMetadata,
      }, user ? { user, role, hasAddressGenerationAccess: true } : null);
    } catch (error) {
      console.error('Failed to track navigation:', error);
    }
  };

  return { trackNavigation };
};
